// Text size: a choice of fitting sizes, not a pixel scale.
//
// Jacky, 2026-10-04: *"AIW currently on my monitor resolution text are toooo small,
// give me a config option to scale (not pixel scale) but select different suitable
// fitting text size."*
//
// What that is and is not:
//   · A NAMED PRESET, picked in the top bar beside the language switch. Each preset
//     is a root size the screens are known to fit at, with the 13px floor raised in
//     step (`@aiw/ui/tokens.css`, `html[data-text-size]`). Not a slider, not a percent.
//   · PER DEVICE, like the language: the monitor is the reason, so the choice belongs
//     to the browser it was made in, never to the staff record or a `bb.config`
//     document. A member on a laptop and the same member on the office monitor each
//     keep their own.
//   · APPLIED BEFORE REACT RENDERS (`main.tsx`), so a reload never flashes small text
//     first, and the sign-in screen is covered too.
import { useCallback, useSyncExternalStore } from "react";

export const TEXT_SIZES = ["compact", "standard", "comfortable", "large", "xlarge"] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

/** The size every screen was designed at. */
export const DEFAULT_TEXT_SIZE: TextSize = "standard";

const KEY = "aiw.crm.textSize";
const EVENT = "aiw:textsize";

export function isTextSize(value: unknown): value is TextSize {
  return typeof value === "string" && (TEXT_SIZES as readonly string[]).includes(value);
}

/** The stored choice; anything unknown (an old value, a typo, no browser) is Standard. */
export function storedTextSize(): TextSize {
  try {
    const v = globalThis.localStorage?.getItem(KEY);
    return isTextSize(v) ? v : DEFAULT_TEXT_SIZE;
  } catch {
    return DEFAULT_TEXT_SIZE;
  }
}

/** Puts the preset on <html>; the CSS does the rest. */
export function applyTextSize(size: TextSize, doc: { documentElement: { dataset: DOMStringMap } } | undefined = globalThis.document): void {
  if (!doc) return;
  doc.documentElement.dataset.textSize = size;
}

/** Remember and apply, and tell every mounted control. */
export function setTextSize(size: TextSize): void {
  if (!isTextSize(size)) size = DEFAULT_TEXT_SIZE;
  try { globalThis.localStorage?.setItem(KEY, size); } catch { /* private mode */ }
  applyTextSize(size);
  globalThis.window?.dispatchEvent(new Event(EVENT));
}

function subscribe(changed: () => void) {
  window.addEventListener(EVENT, changed);
  window.addEventListener("storage", changed);
  return () => { window.removeEventListener(EVENT, changed); window.removeEventListener("storage", changed); };
}

/** The control's binding: the current preset and the setter. */
export function useTextSize(): { size: TextSize; setSize: (size: TextSize) => void } {
  const size = useSyncExternalStore(subscribe, storedTextSize, () => DEFAULT_TEXT_SIZE);
  const setSize = useCallback((next: TextSize) => setTextSize(next), []);
  return { size, setSize };
}
